import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  FileText, 
  Upload, 
  Send,
  Download,
  Eye,
  RefreshCw,
  ChevronRight,
  User,
  Calendar,
  Briefcase,
  MapPin,
  Mail,
  Phone
} from 'lucide-react';
import { workflowService } from '@/services/turfai/workflowService';
import { OnboardingSessionManager } from '@/utils/onboardingSession';

interface CompletenessReviewProps {
  executionId: string;
  employeeData: {
    employee_id: string;
    full_name: string;
    department: string;
    start_date?: string;
    joiner_type?: string;
    experience_level?: string;
  };
  onUploadMore?: () => void;
  onSubmitForApproval?: () => void;
  onRequestCorrections?: (items: string[]) => void;
}

interface ExtractedData {
  personal_info?: {
    name?: string;
    date_of_birth?: string;
    gender?: string;
    photo?: string;
  };
  identity_info?: {
    aadhaar_number?: string;
    pan_number?: string;
    passport_number?: string;
  };
  contact_info?: {
    address?: string;
    phone?: string;
    email?: string;
  };
  employment_info?: {
    previous_employer?: string;
    designation?: string;
    experience_years?: number;
  };
}

export const CompletenessReview: React.FC<CompletenessReviewProps> = ({
  executionId,
  employeeData,
  onUploadMore,
  onSubmitForApproval,
  onRequestCorrections
}) => {
  const [loading, setLoading] = useState(true);
  const [validationResults, setValidationResults] = useState<any>(null);
  const [gapAnalysis, setGapAnalysis] = useState<any>(null);
  const [extractedData, setExtractedData] = useState<ExtractedData>({});
  const [documentResults, setDocumentResults] = useState<any[]>([]);
  const [selectedTab, setSelectedTab] = useState('overview');

  useEffect(() => {
    loadExecutionResults();
  }, [executionId]);

  const loadExecutionResults = async () => {
    try {
      const execution = await workflowService.getExecutionStatus(executionId);
      
      if (execution.status === 'completed' && execution.outputs) {
        const report = await workflowService.getValidationReport(executionId);
        setValidationResults(report.validation_results);
        setGapAnalysis(report.gap_analysis);
        
        // Extract data from validation report and transform to expected structure
        if (report.validation_results.extraction_data) {
          const rawData = report.validation_results.extraction_data;
          
          // Transform flat data to structured format
          const structuredData: ExtractedData = {
            personal_info: {
              name: rawData.full_name || '',
              date_of_birth: rawData.date_of_birth || '',
              gender: rawData.gender || ''
            },
            identity_info: {
              aadhaar_number: rawData.aadhaar_number || '',
              pan_number: rawData.pan_number || '',
              passport_number: rawData.passport_number || ''
            },
            contact_info: {
              address: rawData.address || '',
              phone: rawData.phone || rawData.mobile || '',
              email: rawData.email || ''
            },
            employment_info: {
              previous_employer: rawData.previous_employer || '',
              designation: rawData.designation || '',
              experience_years: rawData.experience_years || 0
            }
          };
          
          setExtractedData(structuredData);
        }
        
        if (execution.outputs.document_results) {
          setDocumentResults(execution.outputs.document_results);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading execution results:', error);
      setLoading(false);
    }
  };

  const getCompletenessColor = (score: number) => {
    if (score >= 0.9) return 'text-green-600';
    if (score >= 0.7) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCompletenessMessage = (score: number) => {
    if (score >= 0.9) return 'All required documents are present and valid';
    if (score >= 0.7) return 'Most documents are present, some items need attention';
    return 'Several documents are missing or need correction';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const completenessScore = validationResults?.completeness_score || 0;
  const isComplete = completenessScore >= 0.9;
  const hasMissingDocs = gapAnalysis?.missing_documents?.length > 0;
  const hasInvalidDocs = gapAnalysis?.invalid_documents?.length > 0;

  return (
    <div className="w-full max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl mx-auto flex items-center justify-center mb-6"
        >
          <FileText className="w-10 h-10 text-white" />
        </motion.div>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Document Review & Completeness Check
        </h1>
        <p className="text-gray-600">
          Review your document submission status and extracted information
        </p>
      </div>

      {/* Completeness Overview Card */}
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">Overall Completeness</h2>
              <p className={`text-lg ${getCompletenessColor(completenessScore)}`}>
                {getCompletenessMessage(completenessScore)}
              </p>
            </div>
            
            {/* Large Circular Progress */}
            <div className="relative">
              <svg className="w-40 h-40 transform -rotate-90">
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="none"
                  className="text-gray-200"
                />
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 70}`}
                  strokeDashoffset={`${2 * Math.PI * 70 * (1 - completenessScore)}`}
                  className={`transition-all duration-1000 ${
                    completenessScore >= 0.9 ? 'text-green-500' :
                    completenessScore >= 0.7 ? 'text-yellow-500' :
                    'text-red-500'
                  }`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-4xl font-bold">
                    {Math.round(completenessScore * 100)}%
                  </div>
                  <div className="text-sm text-gray-600">Complete</div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4">
            {isComplete ? (
              <Button 
                onClick={onSubmitForApproval}
                className="bg-green-600 hover:bg-green-700"
              >
                <Send className="h-4 w-4 mr-2" />
                Submit for Approval
              </Button>
            ) : (
              <>
                <Button 
                  onClick={onUploadMore}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Missing Documents
                </Button>
                {hasInvalidDocs && (
                  <Button 
                    variant="outline"
                    onClick={() => onRequestCorrections?.(gapAnalysis.invalid_documents)}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Request Corrections
                  </Button>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Detailed View */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="extracted">Extracted Data</TabsTrigger>
          <TabsTrigger value="gaps">Gap Analysis</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Documents Uploaded</p>
                    <p className="text-2xl font-bold">{documentResults.length}</p>
                  </div>
                  <FileText className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Valid Documents</p>
                    <p className="text-2xl font-bold text-green-600">
                      {documentResults.filter(d => d.status === 'completed').length}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Items Needing Attention</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {(gapAnalysis?.missing_documents?.length || 0) + (gapAnalysis?.invalid_documents?.length || 0)}
                    </p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Employee Info */}
          <Card>
            <CardHeader>
              <CardTitle>Employee Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium">{employeeData.full_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Briefcase className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Department</p>
                    <p className="font-medium">{employeeData.department}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Start Date</p>
                    <p className="font-medium">{employeeData.start_date || 'Not specified'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline">
                    {employeeData.joiner_type} - {employeeData.experience_level}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          {documentResults.map((doc, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${
                      doc.status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'
                    }`}>
                      <FileText className={`h-6 w-6 ${
                        doc.status === 'completed' ? 'text-green-600' : 'text-yellow-600'
                      }`} />
                    </div>
                    <div>
                      <h4 className="font-medium">{doc.document_name}</h4>
                      <p className="text-sm text-gray-600">
                        Type: {doc.classification || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Confidence</p>
                      <p className="font-medium">{Math.round((doc.confidence || 0) * 100)}%</p>
                    </div>
                    {doc.status === 'completed' ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-yellow-500" />
                    )}
                  </div>
                </div>
                
                {/* Validation Score Bar */}
                {validationResults?.document_scores?.[doc.document_name] && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600">Validation Score</span>
                      <span className="font-medium">
                        {Math.round(validationResults.document_scores[doc.document_name] * 100)}%
                      </span>
                    </div>
                    <Progress 
                      value={validationResults.document_scores[doc.document_name] * 100} 
                      className="h-2"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Extracted Data Tab */}
        <TabsContent value="extracted" className="space-y-4">
          {Object.keys(extractedData).length > 0 ? (
            <>
              {extractedData.personal_info && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Personal Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {extractedData.personal_info.name && (
                        <div>
                          <p className="text-sm text-gray-600">Name</p>
                          <p className="font-medium">{extractedData.personal_info.name}</p>
                        </div>
                      )}
                      {extractedData.personal_info.date_of_birth && (
                        <div>
                          <p className="text-sm text-gray-600">Date of Birth</p>
                          <p className="font-medium">{extractedData.personal_info.date_of_birth}</p>
                        </div>
                      )}
                      {extractedData.personal_info.gender && (
                        <div>
                          <p className="text-sm text-gray-600">Gender</p>
                          <p className="font-medium">{extractedData.personal_info.gender}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {extractedData.identity_info && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Identity Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {extractedData.identity_info.aadhaar_number && (
                        <div>
                          <p className="text-sm text-gray-600">Aadhaar Number</p>
                          <p className="font-medium">XXXX-XXXX-{extractedData.identity_info.aadhaar_number.slice(-4)}</p>
                        </div>
                      )}
                      {extractedData.identity_info.pan_number && (
                        <div>
                          <p className="text-sm text-gray-600">PAN Number</p>
                          <p className="font-medium">{extractedData.identity_info.pan_number}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {extractedData.contact_info && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {extractedData.contact_info.address && (
                        <div className="flex items-start gap-3">
                          <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Address</p>
                            <p className="font-medium">{extractedData.contact_info.address}</p>
                          </div>
                        </div>
                      )}
                      {extractedData.contact_info.phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-600">Phone</p>
                            <p className="font-medium">{extractedData.contact_info.phone}</p>
                          </div>
                        </div>
                      )}
                      {extractedData.contact_info.email && (
                        <div className="flex items-center gap-3">
                          <Mail className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-600">Email</p>
                            <p className="font-medium">{extractedData.contact_info.email}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Alert>
              <AlertDescription>
                No data has been extracted yet. Please ensure all documents are processed.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* Gap Analysis Tab */}
        <TabsContent value="gaps" className="space-y-4">
          {hasMissingDocs && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <XCircle className="h-5 w-5" />
                  Missing Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {gapAnalysis.missing_documents.map((doc: string, index: number) => (
                    <Badge key={index} variant="destructive" className="py-1 px-3">
                      {doc}
                    </Badge>
                  ))}
                </div>
                <Button 
                  className="mt-4"
                  onClick={onUploadMore}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Missing Documents
                </Button>
              </CardContent>
            </Card>
          )}

          {hasInvalidDocs && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-yellow-700">
                  <AlertTriangle className="h-5 w-5" />
                  Documents Needing Correction
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {gapAnalysis.invalid_documents.map((doc: string, index: number) => (
                    <Badge key={index} variant="secondary" className="py-1 px-3">
                      {doc}
                    </Badge>
                  ))}
                </div>
                <Button 
                  className="mt-4"
                  variant="outline"
                  onClick={() => onRequestCorrections?.(gapAnalysis.invalid_documents)}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Request Corrections
                </Button>
              </CardContent>
            </Card>
          )}

          {gapAnalysis?.recommendations?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recommendations</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {gapAnalysis.recommendations.map((rec: string, index: number) => (
                    <li key={index} className="flex items-start gap-2">
                      <ChevronRight className="h-4 w-4 text-gray-400 mt-0.5" />
                      <span className="text-sm">{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {!hasMissingDocs && !hasInvalidDocs && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <AlertDescription className="ml-2 text-green-800">
                Excellent! All required documents are present and valid. You can proceed with submission.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>

      {/* Final Action */}
      {isComplete && (
        <div className="text-center mt-8">
          <Button 
            size="lg"
            onClick={onSubmitForApproval}
            className="bg-green-600 hover:bg-green-700"
          >
            <Send className="h-5 w-5 mr-2" />
            Submit for Final Approval
          </Button>
          <p className="text-sm text-gray-600 mt-2">
            All documents have been validated and are ready for submission
          </p>
        </div>
      )}
    </div>
  );
};